import React from "react";
import { Box, Container, Typography } from "@mui/material";
import UserLeaderBoard from "./UserLeaderBoard";
import UserProfileScreen from "./UserProfileScreen";
import { styled, alpha } from "@mui/material/styles";
import {
  Card,
  CardContent,
  CardMedia,
  CardActions,
  CircularProgress,
  Drawer,
} from "@mui/material";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import StudentDashboard from "./StudentDashboard";
import GradeComponent from "./GradeComponent";

const CourseCard = styled(Card)(({ theme }) => ({
  width: 320,
  margin: theme.spacing(1),
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const ToggleButton = styled(Button)(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: active
    ? theme.palette.primary.contrastText
    : theme.palette.primary.main,
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.dark
      : alpha(theme.palette.primary.main, 0.08),
  },
}));

const grades = [
  { courseName: "Not Available", grade: "A", semester: "Fall 2023" },
  { courseName: "Not Available", grade: "B+", semester: "Fall 2023" },
  { courseName: "Not Available", grade: "A-", semester: "Spring 2024" },
  { courseName: "Not Available", grade: "B", semester: "Spring 2024" },
  { courseName: "Not Available", grade: "A", semester: "Summer 2024" },
  { courseName: "Not Available", grade: "B+", semester: "Summer 2024" },
  { courseName: "Not Available", grade: "A-", semester: "Fall 2024" },
  { courseName: "Not Available", grade: "B", semester: "Fall 2024" },
];

const MainContent = ({
  currentScreen,
  isLoading,
  awarnessCourses,
  isAwarenessAssigned,
  setIsAwarenessAssigned,
  onCourseClick,
  onCourseDetailClick,
}) => {
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {currentScreen === 10 && <UserLeaderBoard
        onCourseClick={onCourseClick}
        onCourseDetailClick={onCourseDetailClick} />}
      {currentScreen === 7 && <UserProfileScreen />}
      {currentScreen === 8 && <GradeComponent grades={grades} />}
      {currentScreen === 0 && (
        <Box>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <CourseSection
              courses={awarnessCourses}
              isAssigned={isAwarenessAssigned}
              onAssignedToggle={() => setIsAwarenessAssigned(true)}
              onCompletedToggle={() => setIsAwarenessAssigned(false)}
              onCourseClick={onCourseClick}
              onCourseDetailClick={onCourseDetailClick}
            />
          )}
        </Box>
      )}
    </Container>
  );
};

const CourseSection = ({
  courses,
  isAssigned,
  onAssignedToggle,
  onCompletedToggle,
  onCourseClick,
  onCourseDetailClick
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Active Courses
        </Typography>
        <Box sx={{
          display: 'flex',
          backgroundColor: 'background.paper',
          borderRadius: 1,
          p: 0.5
        }}>
          <ToggleButton
            active={isAssigned}
            onClick={onAssignedToggle}
            sx={{ borderRadius: '4px 0 0 4px' }}
          >
            Assigned
          </ToggleButton>
          <ToggleButton
            active={!isAssigned}
            onClick={onCompletedToggle}
            sx={{ borderRadius: '0 4px 4px 0' }}
          >
            Completed
          </ToggleButton>
        </Box>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 2,
        overflowX: 'auto',
        pb: 2,
        '@media (max-width: 1200px)': {
          gridTemplateColumns: 'repeat(3, 1fr)',
        },
        '@media (max-width: 900px)': {
          gridTemplateColumns: 'repeat(2, 1fr)',
        },
        '@media (max-width: 600px)': {
          gridTemplateColumns: 'repeat(1, 1fr)',
        },
      }}>
        {(isAssigned ? courses : courses.filter(e => e.isFinal)).map((course, index) => {
          const percentCompleted = Math.min(
            ((course.currentProgress.length / course.total) * 100) || 0,
            100
          );

          return (
            <CourseCard
              key={index}
              onClick={() => onCourseClick(course)}
              sx={{
                width: '100%',
                height: '100%'
              }}
            >
              <CardMedia
                component="img"
                height="180"
                image={course.thumbnail}
                alt={course.title}
                sx={{ objectFit: 'cover' }}
              />
              <CardContent>
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{
                    fontWeight: 500,
                    mb: 2,
                    height: 48,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {course.description}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={percentCompleted}
                      sx={{ flexGrow: 1, mr: 2, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {Math.round(percentCompleted)}%
                    </Typography>
                  </Box>
                </Box>
                {course.isCompleted && (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      onCourseDetailClick(course);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Resume Course
                  </Button>
                )}
              </CardContent>
            </CourseCard>
          );
        })}
      </Box>
    </Box>
  );
};

export default MainContent;
